import { Checkbox, Divider, FormControlLabel, FormGroup, Grid, Typography } from '@mui/material';
import { VTextField, VTextFieldCEP, VTextFieldCNPJ } from '../../../forms';
import { PAYMENT_METHODS } from '../../../constants/paymentMethods';
import { DayScheduleEditor, IDaySchedule } from '../dayScheduleEditor/DayScheduleEditor';
import * as Yup from 'yup';
import { useField } from '@unform/core';
import { useEffect, useState } from 'react';

export const createBusinessFormSchema = Yup.object().shape({
  name: Yup.string().required('O nome do estabelecimento é obrigatório'),
  street: Yup.string().required('Rua é obrigatório'),
  streetNumber: Yup.string().required('Número é obrigatório'),
  neighborhood: Yup.string().required('Bairro é obrigatório'),
  city: Yup.string().required('Cidade é obrigatório'),
  zipCode: Yup.string(),
  cnpj: Yup.string(),
  phone: Yup.string(),
  instagram: Yup.string(),
  facebook: Yup.string(),
  website: Yup.string(),
  paymentMethods: Yup.string(),
});

interface CreateBusinessFormBodyProps {
  schedule: IDaySchedule[];
  onScheduleChange: (schedule: IDaySchedule[]) => void;
}

export const CreateBusinessFormBody: React.FC<CreateBusinessFormBodyProps> = ({
  schedule,
  onScheduleChange,
}) => {
  const { fieldName, registerField, defaultValue, clearError } = useField('paymentMethods');
  const [paymentMethods, setPaymentMethods] = useState<string[]>(() => {
    if (defaultValue) return defaultValue.split(', ');
    return [];
  });

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => paymentMethods.length > 0 ? paymentMethods.join(', ') : '',
      setValue: (_, newValue: string) => setPaymentMethods(newValue ? newValue.split(', ') : []),
      clearValue: () => setPaymentMethods([]),
    });
  }, [registerField, fieldName, paymentMethods]);

  return (
    <>
      <Divider />
      <Typography variant='subtitle1' fontWeight={700}>
        Dados do Estabelecimento
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={7}>
          <VTextField name='name' label='Nome' required />
        </Grid>
        <Grid item xs={5}>
          <VTextFieldCNPJ name='cnpj' label='CNPJ' />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={8}>
          <VTextField name='street' label='Rua' required />
        </Grid>
        <Grid item xs={4}>
          <VTextField name='streetNumber' label='Número' required />
        </Grid>
        <Grid item xs={12}>
          <VTextField name='neighborhood' label='Bairro' required />
        </Grid>
        <Grid item xs={7}>
          <VTextField name='city' label='Cidade' required />
        </Grid>
        <Grid item xs={5}>
          <VTextFieldCEP name='zipCode' label='CEP' placeholder='00000-000' />
        </Grid>
      </Grid>

      <Divider />
      <Typography variant='subtitle1' fontWeight={700}>
        Contato e Redes Sociais
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <VTextField name='phone' label='Telefone (opcional)' placeholder='(51) 99999-9999' />
        </Grid>
        <Grid item xs={6}>
          <VTextField name='instagram' label='Instagram (opcional)' placeholder='@seudominio' />
        </Grid>
        <Grid item xs={6}>
          <VTextField name='facebook' label='Facebook (opcional)' placeholder='https://facebook.com/seudominio' />
        </Grid>
        <Grid item xs={6}>
          <VTextField name='website' label='Website (opcional)' placeholder='https://meusite.com.br' />
        </Grid>
      </Grid>

      <Divider />
      <Typography variant='subtitle1' fontWeight={700}>
        Formas de Pagamento
      </Typography>

      <FormGroup row>
        {PAYMENT_METHODS.map((method) => (
          <FormControlLabel
            key={method}
            control={
              <Checkbox
                checked={paymentMethods.includes(method)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setPaymentMethods([...paymentMethods, method]);
                  } else {
                    setPaymentMethods(paymentMethods.filter((m) => m !== method));
                  }
                  clearError();
                }}
              />
            }
            label={method}
          />
        ))}
      </FormGroup>

      <Divider />
      <Typography variant='subtitle1' fontWeight={700}>
        Horários de Funcionamento
      </Typography>

      <DayScheduleEditor
        schedule={schedule}
        onChange={onScheduleChange}
        defaultStartTime={null}
        defaultEndTime={null}
      />
    </>
  );
};
